'use client';

import { createContext, useCallback, useContext, useEffect, useState } from 'react';

const MAX_SPLASH_MS = 5000;
const FADE_MS = 400;

const SplashContext = createContext<{ setContentReady: () => void }>({
  setContentReady: () => {},
});

export const useSplash = () => useContext(SplashContext);

export function SplashProvider({ children }: { children: React.ReactNode }) {
  const [contentReady, setReady] = useState(false);
  const setContentReady = useCallback(() => setReady(true), []);

  return (
    <SplashContext.Provider value={{ setContentReady }}>
      {children}
      <SplashOverlay ready={contentReady} />
    </SplashContext.Provider>
  );
}

function SplashOverlay({ ready }: { ready: boolean }) {
  const [visible, setVisible] = useState(true);
  const [fadeOut, setFadeOut] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setFadeOut(true), MAX_SPLASH_MS);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    if (ready && !fadeOut) {
      const t = setTimeout(() => setFadeOut(true), 150);
      return () => clearTimeout(t);
    }
  }, [ready, fadeOut]);

  useEffect(() => {
    if (fadeOut) {
      const t = setTimeout(() => setVisible(false), FADE_MS);
      return () => clearTimeout(t);
    }
  }, [fadeOut]);

  if (!visible) return null;

  return (
    <div
      className="fixed inset-0 z-[9999] flex flex-col items-center justify-center"
      style={{
        background: 'rgba(6, 6, 6, 0.85)',
        backdropFilter: 'blur(24px)',
        WebkitBackdropFilter: 'blur(24px)',
        transition: `opacity ${FADE_MS}ms ease`,
        opacity: fadeOut ? 0 : 1,
        pointerEvents: fadeOut ? 'none' : 'auto',
      }}
    >
      <div
        className="h-28 w-24"
        style={{
          backgroundColor: '#81B4CE',
          WebkitMaskImage: 'url(/assets/logo-full.svg)',
          maskImage: 'url(/assets/logo-full.svg)',
          WebkitMaskSize: 'contain',
          maskSize: 'contain',
          WebkitMaskRepeat: 'no-repeat',
          maskRepeat: 'no-repeat',
          WebkitMaskPosition: 'center',
          maskPosition: 'center',
        }}
      />

      <p
        className="mt-8 text-base text-[#fafafa]"
        style={{ fontFamily: 'Inter, sans-serif', letterSpacing: '-0.4px' }}
      >
        Загрузка...
      </p>

      <div className="mt-8 flex gap-2.5">
        <span className="splash-dot" />
        <span className="splash-dot" style={{ animationDelay: '150ms' }} />
        <span className="splash-dot" style={{ animationDelay: '300ms' }} />
      </div>
    </div>
  );
}
